import { useState, useEffect, useRef } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

interface Item {
  id: number
  name: string
}

interface MultiCheckboxSelectProps {
  /** Static list — filtered client-side by cmdk */
  items?: Item[]
  /** Async search — called server-side; disables cmdk filtering */
  fetchItems?: (search: string) => Promise<Item[]>
  /** Seed resolved names for pre-selected ids (edit mode) */
  initialSelectedItems?: Item[]
  selected: number[]
  onChange: (ids: number[]) => void
  placeholder: string
  allLabel: string
  emptyLabel?: string
  className?: string
}

export function MultiCheckboxSelect({
  items,
  fetchItems,
  initialSelectedItems,
  selected,
  onChange,
  placeholder,
  allLabel,
  emptyLabel = '—',
  className,
}: MultiCheckboxSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [fetchedItems, setFetchedItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(false)
  // Persistent name cache so trigger badges keep their labels after search changes
  const resolvedRef = useRef<Map<number, string>>(new Map())

  const isAsync = !!fetchItems

  // Seed cache from initial selection (edit mode pre-populated values)
  useEffect(() => {
    initialSelectedItems?.forEach((i) => resolvedRef.current.set(i.id, i.name))
  }, [initialSelectedItems])

  // Keep cache updated as results come in
  useEffect(() => {
    fetchedItems.forEach((i) => resolvedRef.current.set(i.id, i.name))
  }, [fetchedItems])

  // Server-side search with 300 ms debounce (instant on open)
  useEffect(() => {
    if (!isAsync || !open) return
    let cancelled = false
    const timer = setTimeout(
      async () => {
        setLoading(true)
        try {
          const results = await fetchItems!(search)
          if (!cancelled) setFetchedItems(results)
        } finally {
          if (!cancelled) setLoading(false)
        }
      },
      search ? 300 : 0,
    )
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [search, open, isAsync, fetchItems])

  const toggle = (id: number) => {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id])
    setSearch('')
  }

  const handleOpenChange = (v: boolean) => {
    setOpen(v)
    if (!v) setSearch('')
  }

  const displayItems = isAsync ? fetchedItems : (items ?? [])

  const selectedItems: Item[] = isAsync
    ? selected.map((id) => ({ id, name: resolvedRef.current.get(id) ?? '…' }))
    : (items ?? []).filter((i) => selected.includes(i.id))

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('h-9 w-full justify-between font-normal', className)}
        >
          <span className="flex flex-wrap gap-1 overflow-hidden flex-1 min-w-0">
            {selected.length === 0 ? (
              <span className="text-muted-foreground text-sm">{allLabel}</span>
            ) : selectedItems.length <= 2 ? (
              selectedItems.map((i) => (
                <Badge key={i.id} variant="secondary" className="text-xs font-normal">
                  {i.name}
                </Badge>
              ))
            ) : (
              <Badge variant="secondary" className="text-xs font-normal">
                {selected.length}
              </Badge>
            )}
          </span>
          <ChevronDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command shouldFilter={!isAsync}>
          <CommandInput placeholder={placeholder} value={search} onValueChange={setSearch} />
          <CommandList>
            {loading ? (
              <div className="py-4 text-center text-xs text-muted-foreground">…</div>
            ) : (
              <>
                <CommandEmpty>{emptyLabel}</CommandEmpty>
                <CommandGroup>
                  {displayItems.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={item.name}
                      onSelect={() => toggle(item.id)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          'me-2 h-4 w-4',
                          selected.includes(item.id) ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {item.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
